/**
 * GenerationPromptsPanel — AI Center "Generation Prompts" sub-tab.
 *
 * Lets an admin view the built-in default and edit a per-tenant override for
 * the two system instructions the AI uses when generating custom-tool content:
 *   - Code Generation   → POST /admin/ai/tools/generate-code
 *   - Prompt Generation → POST /admin/ai/agents/generate-prompt
 *
 * The override text is sent verbatim (after {{placeholder}} substitution the
 * server does). Nothing is enforced — the warning callout is the only guard.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Wand2, RotateCcw, AlertTriangle, Copy, Check, Loader2 } from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'

type SectionKey = 'code' | 'prompt'

interface PromptState {
  default: string
  override: string | null
}

interface GenerationPromptsResponse {
  code: PromptState
  prompt: PromptState
  placeholders: { code: string[]; prompt: string[] }
}

const SECTION_META: Record<SectionKey, { title: string; blurb: string; placeholderHelp: Record<string, string> }> = {
  code: {
    title: 'Code Generation',
    blurb: 'System instruction for "Generate Code" in the Custom Tool editor (custom_code tools). Sent verbatim — the language contract, capability list and few-shot examples shown in the default are all part of the text you can edit.',
    placeholderHelp: {},
  },
  prompt: {
    title: 'Prompt Generation',
    blurb: 'System instruction for "Generate Prompt" when authoring agent / tool system prompts.',
    placeholderHelp: {
      '{{targetContext}}': 'the role-aware context block (main orchestrator / sub-orchestrator / agent, incl. runtime facts)',
    },
  },
}

export default function GenerationPromptsPanel(): JSX.Element {
  const toast = useToast()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<GenerationPromptsResponse | null>(null)
  const [drafts, setDrafts] = useState<Record<SectionKey, string>>({ code: '', prompt: '' })
  const [saving, setSaving] = useState<SectionKey | null>(null)
  const [copied, setCopied] = useState<SectionKey | null>(null)

  const load = async (): Promise<void> => {
    setLoading(true)
    try {
      const res = await api.get<GenerationPromptsResponse>('/admin/ai/tools/generation-prompts')
      setData(res)
      setDrafts({
        code: res.code.override ?? res.code.default,
        prompt: res.prompt.override ?? res.prompt.default,
      })
    } catch (err) {
      toast.error('Failed to load generation prompts', err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const effectiveStored = useMemo(() => {
    if (!data) return { code: '', prompt: '' }
    return {
      code: data.code.override ?? data.code.default,
      prompt: data.prompt.override ?? data.prompt.default,
    }
  }, [data])

  const save = async (key: SectionKey, value: string | null): Promise<void> => {
    setSaving(key)
    try {
      const res = await api.put<GenerationPromptsResponse>('/admin/ai/tools/generation-prompts', { [key]: value })
      setData(res)
      setDrafts({
        code: res.code.override ?? res.code.default,
        prompt: res.prompt.override ?? res.prompt.default,
      })
      toast.success(
        value === null ? `${SECTION_META[key].title} reset to default` : `${SECTION_META[key].title} saved`,
      )
    } catch (err) {
      toast.error('Save failed', err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(null)
    }
  }

  const copyDefault = async (key: SectionKey): Promise<void> => {
    if (!data) return
    try {
      await navigator.clipboard.writeText(data[key].default)
      setCopied(key)
      setTimeout(() => setCopied(null), 1500)
    } catch {
      /* clipboard unavailable — no-op */
    }
  }

  if (loading || !data) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {(['code', 'prompt'] as SectionKey[]).map((key) => {
        const meta = SECTION_META[key]
        const section = data[key]
        const draft = drafts[key]
        const isOverridden = section.override !== null
        const dirty = draft !== effectiveStored[key]
        return (
          <section key={key} className="rounded-sm border border-gray-200 bg-white">
            <header className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-[#3454cf]/10 text-[#3454cf]">
                  <Wand2 className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-gray-900">{meta.title}</h2>
                  <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{meta.blurb}</p>
                  <p className="mt-1 text-[11px] font-medium text-gray-400">
                    {isOverridden ? 'Custom override active' : 'Using built-in default'}
                  </p>
                </div>
              </div>
            </header>

            <div className="min-w-0 space-y-4 px-4 py-4">
              <div className="flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  This text is sent verbatim as the system instruction. Removing the sandbox / safety
                  rules can break or weaken tool generation.
                </span>
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">Override</span>
                  <button
                    type="button"
                    disabled={!isOverridden || saving === key}
                    onClick={() => void save(key, null)}
                    className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] font-medium text-[#3454cf] transition-colors hover:bg-[#3454cf]/10 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Reset to default
                  </button>
                </div>
                <textarea
                  value={draft}
                  onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                  rows={14}
                  spellCheck={false}
                  className="w-full rounded-sm border border-gray-300 bg-white px-3 py-2 font-mono text-xs leading-relaxed text-gray-900 focus:border-[#3454cf] focus:outline-none focus:ring-1 focus:ring-[#3454cf]"
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={!dirty || draft.trim().length === 0 || saving === key}
                    onClick={() => void save(key, draft)}
                    className="inline-flex items-center gap-1.5 rounded-sm bg-[#3454cf] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#2a45b0] disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {saving === key && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Save
                  </button>
                  {dirty && <span className="text-[11px] text-gray-400">Unsaved changes</span>}
                </div>
              </div>

              {data.placeholders[key].length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold text-gray-700">Placeholders the server substitutes</p>
                  <ul className="space-y-0.5 text-[11px] text-gray-500">
                    {data.placeholders[key].map((token) => (
                      <li key={token}>
                        <code className="rounded-sm bg-gray-100 px-1 py-0.5 font-mono text-[10px] text-gray-700">
                          {token}
                        </code>{' '}
                        — {meta.placeholderHelp[token] ?? 'runtime value'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <details className="rounded-sm border border-gray-200 bg-gray-50">
                <summary className="cursor-pointer select-none px-3 py-2 text-xs font-semibold text-gray-700">
                  Built-in default
                </summary>
                <div className="border-t border-gray-200 px-3 py-2">
                  <div className="mb-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => void copyDefault(key)}
                      className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] font-medium text-gray-500 transition-colors hover:bg-gray-200"
                    >
                      {copied === key ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      {copied === key ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-sm bg-white p-2 font-mono text-[11px] leading-relaxed text-gray-700">
                    {section.default}
                  </pre>
                </div>
              </details>
            </div>
          </section>
        )
      })}
    </div>
  )
}
